import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

// status: 1 = Approve with PTO, 2 = Approve without PTO
const OPTIONS = [
  {
    status: 1,
    title: "Approve with PTO",
    description: "Deduct from available balance",
  },
  {
    status: 2,
    title: "Approve without PTO",
    description: "Recorded as unpaid time off",
  },
];

const WIDTH = 320;

// Popover that opens below its trigger button. Rendered in a portal with
// fixed positioning so it escapes the table's overflow-hidden container.
// The parent handles outside-click; `anchorRef` is the trigger element.
function ApprovalPopover({ anchorRef, onConfirm }) {
  const [selected, setSelected] = useState(1);
  const [pos, setPos] = useState(null);

  // reset to the default option each time the popover mounts (opens)
  useEffect(() => {
    setSelected(1);
  }, []);

  // position the popover below and right-aligned to the trigger
  useLayoutEffect(() => {
    const place = () => {
      const el = anchorRef?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const buttonCenterX = rect.left + rect.width / 2;
      // center the popover under the button, clamped to the viewport
      let left = buttonCenterX - WIDTH / 2;
      left = Math.min(Math.max(left, 8), window.innerWidth - WIDTH - 8);
      setPos({
        top: rect.bottom + 6,
        left,
        // caret points at the button's center (12px caret, so -6)
        arrowLeft: Math.min(Math.max(buttonCenterX - left - 6, 12), WIDTH - 24),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef]);

  if (!pos) return null;

  return createPortal(
    <div
      data-approval-popover
      className="fixed z-[60]"
      style={{ top: pos.top, left: pos.left, width: WIDTH, maxWidth: "90vw" }}
    >
      {/* arrow pointing up at the trigger. The white square covers the
          card's top border at the caret so there's no seam line, and a
          slightly larger border-colored square behind it draws the caret
          outline. */}
      <div className="absolute -top-[7px] w-3.5 h-3.5 rotate-45 bg-[#EDEDED]" style={{ left: pos.arrowLeft - 1 }} />
      <div className="absolute -top-1.5 z-10 w-3 h-3 rotate-45 bg-white" style={{ left: pos.arrowLeft }} />
      <div className="relative h-[298px] rounded-lg border border-[#EDEDED] bg-white p-5 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.10),0_4px_6px_-2px_rgba(0,0,0,0.05)]">
        <h2 className="font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] capitalize text-[#1F1F1F]">
          Approve Time Off Request
        </h2>
        <p className="mt-1 font-OpenSansRegular text-[12px] not-italic leading-[18px] tracking-[0.12px] text-[#7C829A]">
          Choose your approval method.
        </p>

        <div className="mt-4 flex flex-col gap-2.5">
          {OPTIONS.map((opt) => {
            const isActive = selected === opt.status;
            return (
              <button
                key={opt.status}
                type="button"
                onClick={() => setSelected(opt.status)}
                className={`flex items-center gap-3 w-full rounded-md border px-3.5 py-3 text-left transition-colors ${
                  isActive ? "border-[#0065FD] bg-[#F5F9FF]" : "border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]"
                }`}
              >
                {isActive ? (
                  <img src="/images/timeoff/active_radio.svg" alt="" className="flex-shrink-0 w-[18px] h-[18px]" />
                ) : (
                  <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full border-2 border-[#CBD5E1]" />
                )}
                <span className="flex flex-col">
                  <span className="font-OpenSansSemiBold text-[14px] not-italic font-semibold leading-5 tracking-[0.14px] text-[#1A2332]">
                    {opt.title}
                  </span>
                  <span className="font-OpenSansRegular text-[12px] not-italic leading-[18px] tracking-[0.12px] text-[#7C829A]">
                    {opt.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => onConfirm(selected)}
          className="mt-4 w-full rounded-lg bg-[#0065FD] py-3 font-OpenSansSemiBold text-[16px] font-semibold leading-6 capitalize text-white hover:bg-[#0065FD]"
        >
          Confirm Approval
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default ApprovalPopover;
